// src/stores/groupStore.ts
/**
 * Zustand store for group management
 * Integrates with existing store pattern in Flowi
 * Now includes support for reusable group instances with master-instance sync
 */

import { create } from 'zustand';
import { groupManager } from '@/features/groups/GroupManager';
import { groupInstanceManager } from '@/features/groups/GroupInstanceManager';
import { ElementGroup, GroupableElement, GroupOperationResult } from '@/features/groups/groupTypes';
import { GroupInstance, GroupInstanceSyncResult } from '@/types/database.types';

interface GroupState {
    /** All local groups (within diagram) */
    groups: ElementGroup[];

    /** Currently selected group IDs */
    selectedGroupIds: string[];

    /** All group instances (references to master groups) */
    groupInstances: GroupInstance[];

    /** Currently selected group instance IDs */
    selectedGroupInstanceIds: string[];

    /** Actions */
    actions: {
        /** Create new group */
        createGroup: (elements: GroupableElement[], name?: string) => GroupOperationResult;

        /** Ungroup */
        ungroup: (groupId: string) => GroupOperationResult;

        /** Add element to group */
        addToGroup: (groupId: string, element: GroupableElement) => GroupOperationResult;

        /** Remove element from group */
        removeFromGroup: (element: GroupableElement) => GroupOperationResult;

        /** Select group */
        selectGroup: (groupId: string, multi?: boolean) => void;

        /** Deselect group */
        deselectGroup: (groupId: string) => void;

        /** Clear selection */
        clearSelection: () => void;

        /** Set group locked state */
        setLocked: (groupId: string, locked: boolean) => void;

        /** Rename group */
        renameGroup: (groupId: string, name: string) => GroupOperationResult;

        /** Set master group ID (link to Supabase) */
        setMasterGroupId: (groupId: string, masterGroupId: string) => GroupOperationResult;

        /** Get elements in group */
        getGroupElements: (groupId: string, recursive?: boolean) => GroupableElement[];

        /** Check if element is grouped */
        isGrouped: (element: GroupableElement) => boolean;

        /** Get group for element */
        getGroupForElement: (element: GroupableElement) => ElementGroup | undefined;

        /** Refresh groups from manager */
        refreshGroups: () => void;

        // ============================================================================
        // Group Instance Actions (Master-Instance Sync)
        // ============================================================================

        /** Create a new instance from a master group */
        createGroupInstance: (masterGroupId: string, position: { x: number; y: number }) => Promise<GroupInstance | null>;

        /** Sync a specific instance with its master group */
        syncGroupInstance: (instanceId: string) => Promise<GroupInstanceSyncResult>;

        /** Sync all instances in the diagram */
        syncAllGroupInstances: () => Promise<Map<string, GroupInstanceSyncResult>>;

        /** Detach instance from master (convert to standalone group) */
        detachGroupInstance: (instanceId: string) => void;

        /** Toggle sync enabled/disabled for an instance */
        toggleInstanceSync: (instanceId: string, enabled: boolean) => void;

        /** Update instance position on canvas */
        updateInstancePosition: (instanceId: string, position: { x: number; y: number }) => void;

        /** Delete a group instance */
        deleteGroupInstance: (instanceId: string) => void;

        /** Get instance by ID */
        getInstance: (instanceId: string) => GroupInstance | undefined;

        /** Get all instances of a master group */
        getInstancesByMasterGroup: (masterGroupId: string) => GroupInstance[];

        /** Get instances that are out of sync */
        getOutOfSyncInstances: () => Promise<GroupInstance[]>;

        /** Select group instance */
        selectGroupInstance: (instanceId: string, multi?: boolean) => void;

        /** Deselect group instance */
        deselectGroupInstance: (instanceId: string) => void;

        /** Refresh group instances from manager */
        refreshGroupInstances: () => void;
    };
}

export const useGroupStore = create<GroupState>((set) => {
    // Subscribe to group manager changes
    groupManager.subscribe(() => {
        set({ groups: groupManager.getAllGroups() });
    });

    // Subscribe to group instance manager changes
    groupInstanceManager.subscribe(() => {
        set({ groupInstances: groupInstanceManager.getAllInstances() });
    });

    return {
        groups: [],
        selectedGroupIds: [],
        groupInstances: [],
        selectedGroupInstanceIds: [],

        actions: {
            createGroup: (elements, name) => {
                const result = groupManager.createGroup(elements, name);
                if (result.success) {
                    set({ groups: groupManager.getAllGroups() });
                }
                return result;
            },

            ungroup: (groupId) => {
                const result = groupManager.ungroup(groupId);
                if (result.success) {
                    set(state => ({
                        groups: groupManager.getAllGroups(),
                        selectedGroupIds: state.selectedGroupIds.filter(id => id !== groupId)
                    }));
                }
                return result;
            },

            addToGroup: (groupId, element) => {
                const result = groupManager.addToGroup(groupId, element);
                if (result.success) {
                    set({ groups: groupManager.getAllGroups() });
                }
                return result;
            },

            removeFromGroup: (element) => {
                const result = groupManager.removeFromGroup(element);
                if (result.success) {
                    set({ groups: groupManager.getAllGroups() });
                }
                return result;
            },

            selectGroup: (groupId, multi = false) => {
                set(state => ({
                    selectedGroupIds: multi
                        ? [...state.selectedGroupIds, groupId]
                        : [groupId]
                }));
            },

            deselectGroup: (groupId) => {
                set(state => ({
                    selectedGroupIds: state.selectedGroupIds.filter(id => id !== groupId)
                }));
            },

            clearSelection: () => {
                set({ selectedGroupIds: [] });
            },

            setLocked: (groupId, locked) => {
                groupManager.setGroupLocked(groupId, locked);
                set({ groups: groupManager.getAllGroups() });
            },

            renameGroup: (groupId, name) => {
                const result = groupManager.renameGroup(groupId, name);
                if (result.success) {
                    set({ groups: groupManager.getAllGroups() });
                }
                return result;
            },

            setMasterGroupId: (groupId, masterGroupId) => {
                const result = groupManager.setMasterGroupId(groupId, masterGroupId);
                if (result.success) {
                    set({ groups: groupManager.getAllGroups() });
                }
                return result;
            },

            getGroupElements: (groupId, recursive = false) => {
                return groupManager.getGroupElements(groupId, recursive);
            },

            isGrouped: (element) => {
                return groupManager.isGrouped(element);
            },

            getGroupForElement: (element) => {
                return groupManager.getGroupForElement(element);
            },

            refreshGroups: () => {
                set({ groups: groupManager.getAllGroups() });
            },

            // ============================================================================
            // Group Instance Action Implementations
            // ============================================================================

            createGroupInstance: async (masterGroupId, position) => {
                const instance = await groupInstanceManager.createInstance(masterGroupId, position);
                if (instance) {
                    set({ groupInstances: groupInstanceManager.getAllInstances() });
                }
                return instance;
            },

            syncGroupInstance: async (instanceId) => {
                const result = await groupInstanceManager.syncInstance(instanceId);
                if (result.success && result.updated) {
                    set({ groupInstances: groupInstanceManager.getAllInstances() });
                }
                return result;
            },

            syncAllGroupInstances: async () => {
                const results = await groupInstanceManager.syncAllInstances();
                set({ groupInstances: groupInstanceManager.getAllInstances() });
                return results;
            },

            detachGroupInstance: (instanceId) => {
                const instance = groupInstanceManager.detachInstance(instanceId);
                if (instance) {
                    // Convert instance to local group
                    const elements: GroupableElement[] = instance.items.map(item => ({
                        type: 'ITEM' as const,
                        id: item.id
                    }));

                    groupManager.createGroup(elements, instance.name);

                    set(state => ({
                        groupInstances: groupInstanceManager.getAllInstances(),
                        groups: groupManager.getAllGroups(),
                        selectedGroupInstanceIds: state.selectedGroupInstanceIds.filter(id => id !== instanceId)
                    }));
                }
            },

            toggleInstanceSync: (instanceId, enabled) => {
                groupInstanceManager.toggleSync(instanceId, enabled);
                set({ groupInstances: groupInstanceManager.getAllInstances() });
            },

            updateInstancePosition: (instanceId, position) => {
                groupInstanceManager.updateInstancePosition(instanceId, position);
                set({ groupInstances: groupInstanceManager.getAllInstances() });
            },

            deleteGroupInstance: (instanceId) => {
                groupInstanceManager.deleteInstance(instanceId);
                set(state => ({
                    groupInstances: groupInstanceManager.getAllInstances(),
                    selectedGroupInstanceIds: state.selectedGroupInstanceIds.filter(id => id !== instanceId)
                }));
            },

            getInstance: (instanceId) => {
                return groupInstanceManager.getInstance(instanceId);
            },

            getInstancesByMasterGroup: (masterGroupId) => {
                return groupInstanceManager.getInstancesByMasterGroup(masterGroupId);
            },

            getOutOfSyncInstances: async () => {
                return await groupInstanceManager.getOutOfSyncInstances();
            },

            selectGroupInstance: (instanceId, multi = false) => {
                set(state => ({
                    selectedGroupInstanceIds: multi
                        ? [...state.selectedGroupInstanceIds, instanceId]
                        : [instanceId]
                }));
            },

            deselectGroupInstance: (instanceId) => {
                set(state => ({
                    selectedGroupInstanceIds: state.selectedGroupInstanceIds.filter(id => id !== instanceId)
                }));
            },

            refreshGroupInstances: () => {
                set({ groupInstances: groupInstanceManager.getAllInstances() });
            }
        }
    };
});